import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { FormTextField } from '@personal-dashboard/ui';
import dayjs from 'dayjs';
import Layout from '../../components/Layout';
import PersonDialog from './components/PersonDialog';
import PersonCard from './components/PersonCard';
import { DeletePersonDialog } from './components/DeletePersonDialog';
import { GET_PEOPLE, CREATE_PERSON, UPDATE_PERSON, DELETE_PERSON } from './queries';

interface SearchForm {
  search: string;
}

type SortKey = 'name' | 'city' | 'birthday';

const daysUntilNextBirthday = (birthday?: string): number | null => {
  if (!birthday) return null;
  const bday = dayjs(birthday);
  const now = dayjs().startOf('day');
  let next = now.month(bday.month()).date(bday.date());
  if (next.isBefore(now)) {
    next = next.add(1, 'year');
  }
  return next.diff(now, 'day');
};

const sortPeople = (people: any[], sortKey: SortKey): any[] => {
  const list = [...people];
  switch (sortKey) {
    case 'city':
      return list.sort((a, b) => {
        const ac = (a.city || '').toLowerCase();
        const bc = (b.city || '').toLowerCase();
        if (!ac && !bc) return a.name.localeCompare(b.name);
        if (!ac) return 1;
        if (!bc) return -1;
        return ac.localeCompare(bc) || a.name.localeCompare(b.name);
      });
    case 'birthday':
      return list.sort((a, b) => {
        const da = daysUntilNextBirthday(a.birthday);
        const db = daysUntilNextBirthday(b.birthday);
        if (da === null && db === null) return a.name.localeCompare(b.name);
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      });
    default:
      return list.sort((a, b) => a.name.localeCompare(b.name));
  }
};

const People: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [localPeople, setLocalPeople] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const location = useLocation();

  const searchMethods = useForm<SearchForm>({ defaultValues: { search: '' } });
  const searchValue = searchMethods.watch('search');

  useEffect(() => {
    setSearchTerm(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchVal = params.get('search');
    if (searchVal) {
      setSearchTerm(searchVal);
      searchMethods.setValue('search', searchVal);
    }
  }, [location.search, searchMethods]);

  const { data, loading, error } = useQuery(GET_PEOPLE);

  useEffect(() => {
    if (data?.getPeople) {
      setLocalPeople(data.getPeople);
    }
  }, [data]);

  const [createPerson] = useMutation(CREATE_PERSON);
  const [updatePerson] = useMutation(UPDATE_PERSON);
  const [deletePerson] = useMutation(DELETE_PERSON);

  const handleCreateOrUpdate = (formData: any): Promise<void> => {
    const input = {
      ...formData,
      birthday: formData.birthday ? formData.birthday.toISOString() : null,
      city: formData.city || undefined,
      country: formData.country || undefined,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      notes: formData.notes || undefined,
    };
    delete input.id;

    if (selectedPersonId) {
      const id = selectedPersonId;
      // Optimistic local update first.
      setLocalPeople((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...input } : p))
      );
      setSelectedPersonId(null);

      updatePerson({
        variables: { id, input },
      }).catch((err) => {
        console.error('Update failed:', err);
        setLocalPeople((prev) => prev.map((p) => (p.id === id ? data?.getPeople?.find((x: any) => x.id === id) ?? p : p)));
      });
    } else {
      // Optimistic add with temp id.
      const tempId = `temp-${Date.now()}`;
      const optimistic = { id: tempId, name: input.name, ...input };
      setLocalPeople((prev) => [...prev, optimistic]);

      createPerson({
        variables: { input },
        update: (cache, { data: result }) => {
          const created = result?.createPerson;
          if (created) {
            setLocalPeople((prev) =>
              prev.map((p) => (p.id === tempId ? { ...p, id: created.id } : p))
            );
          }
        },
      }).catch((err) => {
        console.error('Create failed:', err);
        setLocalPeople((prev) => prev.filter((p) => p.id !== tempId));
      });
    }
    return Promise.resolve();
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    const id = deleteTargetId;
    setDeleteTargetId(null);

    // Optimistic removal.
    setLocalPeople((prev) => prev.filter((p) => p.id !== id));

    deletePerson({
      variables: { id },
    })
      .catch((err) => {
        console.error('Delete failed:', err);
        setLocalPeople((prev) => {
          const original = data?.getPeople?.find((p: any) => p.id === id);
          return original ? [...prev, original] : prev;
        });
      })
      .finally(() => setDeleteLoading(false));
  };

  const filteredPeople = useMemo(() => {
    const filtered = localPeople.filter((person: any) =>
      person.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return sortPeople(filtered, sortKey);
  }, [localPeople, searchTerm, sortKey]);

  if (error) return <Alert severity="error">Failed to load people: {error.message}</Alert>;

  const selectedPerson = localPeople.find((p: any) => p.id === selectedPersonId);

  const openAdd = () => {
    setSelectedPersonId(null);
    setIsDialogOpen(true);
  };

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
          People
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }}
        >
          <FormProvider {...searchMethods}>
            <FormTextField
              name="search"
              placeholder="Search people..."
              size="medium"
              sx={{ width: { xs: '100%', sm: 240 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </FormProvider>
          <FormControl size="medium" sx={{ width: { xs: '100%', sm: 190 } }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              label="Sort by"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              startAdornment={<SortIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="city">City</MenuItem>
              <MenuItem value="birthday">Nearest Birthday</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Add Contact
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Typography>Loading network...</Typography>
      ) : (
        <Grid container spacing={2} sx={{ pb: isMobile ? 10 : 0 }}>
          {filteredPeople.map((person: any) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
              <PersonCard
                person={person}
                onEdit={(id) => { setSelectedPersonId(id); setIsDialogOpen(true); }}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {filteredPeople.length === 0 && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2, opacity: 0.6 }}>
          <Typography variant="h6">No contacts found</Typography>
          <Typography variant="body2">Start by adding someone to your network!</Typography>
        </Paper>
      )}

      {isMobile && (
        <Fab
          variant="extended"
          color="primary"
          onClick={openAdd}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            boxShadow: 4,
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add
        </Fab>
      )}

      <PersonDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleCreateOrUpdate}
        initialData={selectedPerson}
        title={selectedPersonId ? 'Edit Contact' : 'Add New Contact'}
      />

      <DeletePersonDialog
        open={!!deleteTargetId}
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Layout>
  );
};

export default People;
